import {
  ItemRarity,
  RARITY_COLORS,
  itemType,
  type InventoryEntry,
} from '@game/protocol';

import { attachItemTooltip } from './item-tooltip.ts';
import { beginSlotDrag } from './slot-drag.ts';

/**
 * O quadradinho onde um item mora, e o arraste que o tira de lá.
 *
 * ## Por que isto saiu do inventário
 *
 * Enquanto equipamento e mochila eram o **mesmo painel**, a célula podia ser um método
 * privado dele: quem desenhava os dez slots do corpo era quem desenhava os vinte da mochila,
 * e a origem do arraste cabia num campo da instância. Separar o equipamento em painel próprio
 * quebrou essa conta em dois lugares.
 *
 * O primeiro é óbvio: duas janelas desenhando a mesma célula seriam duas cópias da regra de
 * raridade, do ícone e da dica — e a segunda ficaria para trás na primeira mudança.
 *
 * O segundo é o que realmente obriga: **o arraste atravessa os dois painéis**. Vestir uma
 * armadura é arrastar da mochila para o corpo, e tirá-la é o contrário. Com a origem guardada
 * numa instância, o painel que recebe o `drop` leria o campo dele — vazio, porque quem
 * começou o gesto foi o outro — e o movimento sumiria. Aqui a origem é do módulo, e os dois
 * painéis leem a mesma variável, que é a única forma de duas janelas não discordarem sobre um
 * gesto que começa numa e termina na outra.
 */

export interface SlotHandlers {
  /** Arrastar e soltar. `count` zero move a pilha inteira. */
  onMove(
    fromContainer: number,
    fromSlot: number,
    toContainer: number,
    toSlot: number,
    count: number,
  ): void;
  /** Clique duplo: beber, vestir, abrir. */
  onUse(container: number, slot: number, typeId: number): void;
}

/**
 * Monta a célula de um slot — vazia se `entry` não vier.
 *
 * `hint` é o nome que aparece no vazio ("Cabeça", "Anel"); contêiner não tem, porque vinte
 * quadrados escritos "mochila" não ajudam ninguém.
 */
export function itemSlot(
  handlers: SlotHandlers,
  container: number,
  slot: number,
  entry: InventoryEntry | undefined,
  hint: string,
): HTMLElement {
  const cell = document.createElement('div');
  cell.className = 'slot';
  if (hint) cell.dataset['hint'] = hint;

  // O endereço do slot fica **no DOM**, e não só no fecho dos ouvintes abaixo: o arraste que
  // começa no cenário é dirigido por ponteiro, não por `dragstart`, e quem o solta precisa
  // descobrir o destino a partir do elemento sob o cursor (`document.elementFromPoint`).
  // Sem estes dois atributos, a cena teria que pedir ao inventário uma tradução de pixel
  // para slot — que é reimplementar, por fora, o que o navegador já resolve.
  cell.dataset['container'] = String(container);
  cell.dataset['slot'] = String(slot);

  if (entry) {
    const type = itemType(entry.typeId);
    // Dica própria no lugar do `title` nativo: o catálogo declara ataque, obtenção e
    // raridade, e o `title` só sabe desenhar uma linha cinza — ver `item-tooltip.ts`.
    if (type) {
      attachItemTooltip(cell, type, entry.count);

      if (type.rarity !== undefined) {
        // A promessa de `RARITY_COLORS` ("o cliente desenha a cor da borda do ícone"),
        // cumprida. Via custom property + classe, e **não** `style.borderColor`: o
        // inline venceria `.slot.drop-target` e o realce verde do arraste sumiria
        // justamente nos slots com raridade.
        cell.classList.add('slot-rarity');
        cell.style.setProperty('--rarity-color', RARITY_COLORS[type.rarity]);

        // As duas faixas que se **mexem**. A borda colorida separa seis degraus parados, e
        // parada ela é lida como categoria: o jogador aprende que âmbar é uma cor de item.
        // Movimento não é uma sétima cor, é outra classe de sinal — o olho o pega pela
        // periferia, sem estar olhando para o slot. É o que faz uma peça de faixa 8 no meio
        // de uma mochila cheia se anunciar sozinha, que é exatamente o que §15.11 promete
        // quando diz que a celestial existe para ser vista.
        if (type.rarity >= ItemRarity.Legendary) {
          cell.classList.add(
            type.rarity >= ItemRarity.Celestial ? 'slot-celestial' : 'slot-legendary',
          );
        }
      }
    }

    const icon = itemIcon(entry.typeId);
    if (icon) {
      const image = document.createElement('img');
      image.src = icon;
      image.alt = type?.name ?? '';
      image.draggable = false;
      cell.append(image);
    } else {
      cell.append(document.createTextNode(type?.name.slice(0, 3) ?? '?'));
    }

    if (entry.count > 1) {
      const count = document.createElement('span');
      count.className = 'slot-count';
      count.textContent = String(entry.count);
      cell.append(count);
    }

    cell.draggable = true;
    cell.addEventListener('dragstart', (event) => {
      dragging = { container, slot };
      cell.classList.add('dragging');

      // A origem vai também no `dataTransfer`, e não só na variável do módulo: a mesa de
      // troca é outro componente e não enxerga este estado. Sem isto, arrastar um item do
      // inventário para a troca exigiria que os dois compartilhassem uma variável — que é a
      // forma mais barata de fazer duas janelas discordarem.
      event.dataTransfer?.setData(
        'application/x-hexaria-item',
        JSON.stringify({ container, slot }),
      );
    });
    cell.addEventListener('dragend', () => {
      dragging = null;
      cell.classList.remove('dragging');
    });

    // O `typeId` vai junto porque o gesto depende do que é o item: uma poção age em quem
    // a bebe, uma runa age no alvo selecionado, e é a cena — que sabe qual é o alvo —
    // quem decide entre os dois. Perguntar de volta ao inventário qual item está naquele
    // slot seria consultar o mesmo estado por um caminho mais longo.
    cell.addEventListener('dblclick', () =>
      handlers.onUse(container, slot, entry.typeId),
    );
  }

  cell.addEventListener('dragover', (event) => {
    event.preventDefault();
    cell.classList.add('drop-target');
  });
  cell.addEventListener('dragleave', () => cell.classList.remove('drop-target'));

  cell.addEventListener('drop', (event) => {
    event.preventDefault();
    cell.classList.remove('drop-target');

    const source = takeDragOrigin();
    if (!source) return;
    if (source.container === container && source.slot === slot) return;

    handlers.onMove(source.container, source.slot, container, slot, 0);
  });

  return cell;
}

// --- Arraste em curso -------------------------------------------------------

/** Origem do arraste em curso, compartilhada por todo painel que desenha slots. */
let dragging: { container: number; slot: number } | null = null;

/**
 * Lê e **consome** a origem do arraste.
 *
 * Consumir, e não só ler: o `drop` acontece antes do `dragend`, e uma origem que ficasse
 * pendurada seria lida de novo pelo próximo `drop` que chegasse sem `dragstart` — um duplo
 * movimento a partir de um slot que o jogador já esvaziou.
 */
export function takeDragOrigin(): { container: number; slot: number } | null {
  const source = dragging;
  dragging = null;
  return source;
}

// --- Ícones -----------------------------------------------------------------

/**
 * O ícone é lido da textura gerada pelo Phaser (`art/items.ts`) e convertido em data URL uma
 * vez por tipo, para o DOM poder desenhá-lo num `<img>` sem um segundo desenho.
 *
 * O cache é do módulo porque a textura é do jogo, e não de uma janela: equipamento,
 * inventário e mesa de troca desenham a mesma espada, e três caches significariam três
 * conversões da mesma textura em base64 — a mais cara das operações desta tela.
 */
const iconCache = new Map<number, string>();
const listeners = new Set<() => void>();

let resolveIcon: (typeId: number) => string | undefined = () => undefined;

/**
 * Fornecido pela cena, que é quem tem as texturas do Phaser em mãos.
 *
 * Chega **depois** dos painéis: eles são montados no `setupInterface` e a cena só resolve
 * ícone quando a arte está carregada. Os painéis que nasceram antes desenharam as três
 * primeiras letras do nome no lugar do desenho, e é por isso que quem se registra em
 * `onIconsChanged` precisa se redesenhar aqui — sem isso, quem abriu o jogo com a mochila
 * cheia ficaria com uma parede de texto até o primeiro item movido.
 */
export function setItemIconResolver(resolve: (typeId: number) => string | undefined): void {
  resolveIcon = resolve;
  iconCache.clear();
  for (const listener of listeners) listener();
}

export function onIconsChanged(listener: () => void): void {
  listeners.add(listener);
}

export function itemIcon(typeId: number): string | undefined {
  const cached = iconCache.get(typeId);
  if (cached) return cached;

  const url = resolveIcon(typeId);
  if (url) iconCache.set(typeId, url);
  return url;
}

// --- Destino de um arraste --------------------------------------------------

/** Destino de um arraste largado sobre a interface. Ver `dropTargetAt`. */
export type DropTarget =
  | { kind: 'slot'; container: number; slot: number }
  /** A faixa "soltar no chão": o tile em que o personagem está. */
  | { kind: 'ground' };

/**
 * O que há sob este ponto da tela, do ponto de vista de um arraste.
 *
 * Mora aqui, e não na cena, porque quem inventou `data-container` foi este módulo: a leitura
 * dos atributos e a escrita deles precisam envelhecer juntas. A cena recebe um destino
 * descrito em termos do jogo e não precisa saber que ele veio de um `<div>`.
 *
 * `element` é o elemento sobre o qual o arraste foi solto — tipicamente o que
 * `document.elementFromPoint` devolveu, que pode ser o `<img>` do ícone e não a célula; daí o
 * `closest`.
 */
export function dropTargetAt(element: Element | null): DropTarget | null {
  const cell = element?.closest<HTMLElement>('.slot[data-container]');
  if (cell) {
    const container = Number(cell.dataset['container']);
    const slot = Number(cell.dataset['slot']);
    if (Number.isFinite(container) && Number.isFinite(slot)) {
      return { kind: 'slot', container, slot };
    }
  }

  return element?.closest('.ground-zone') ? { kind: 'ground' } : null;
}

/** Realce de destino sob o cursor, o mesmo que o arraste nativo do DOM já acende. */
export function highlightDropTarget(element: Element | null): void {
  const next = element?.closest<HTMLElement>('.slot[data-container], .ground-zone') ?? null;
  if (next === highlighted) return;

  highlighted?.classList.remove('drop-target');
  next?.classList.add('drop-target');
  highlighted = next;
}

export function clearDropHighlight(): void {
  highlightDropTarget(null);
}

let highlighted: HTMLElement | null = null;
